/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/* eslint-disable @elastic/eui/prefer-css-attributes-for-eui-components */

import React, { useState } from 'react'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiLink,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSpacer,
  useEuiTheme,
  useGeneratedHtmlId,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import genericMessages from '@modules/project-lib/genericMessages'
import type { AnySecurityProject } from '@modules/project-api/types'
import {
  includesProductType,
  setSecurityTier,
  toggleSecurityLine,
} from '@modules/project-lib/securityProjectTypes'
import { getQuerystringItem } from '@modules/utils/qs'
import usePermissions from '@modules/permissions-lib/hooks'
import { useSearchAILakeAvailable } from '@modules/project-lib/hooks/useSearchAILakeAvailable'
import { DocLink } from '@modules/doclinks'

import { SecurityProductFeatureOptions } from './SecurityProductFeatureOptions'
import { PRODUCT_TYPES, DEFAULT_VALUE } from './ProductDefinition'

import type { SecurityProductTier, SecurityProductType } from '../../project-user-api/v1/types'

export type Props = {
  project: AnySecurityProject
  onChange: (productTypes: AnySecurityProject['product_types'], onSettled: () => void) => void
  isLoading: boolean
}

const SecurityProjectFeatures: React.FunctionComponent<Props> = ({
  project,
  onChange,
  isLoading,
}) => {
  const htmlId = useGeneratedHtmlId()
  const { euiTheme } = useEuiTheme()

  const { hasPermissions: hasUpdatePermission } = usePermissions([
    { type: `project-${project.type}`, action: 'update', id: project.id },
  ])

  const [isEditing, setIsEditing] = useState(
    getQuerystringItem('open') === 'securityProjectFeatures',
  )
  const [productTypes, setProductTypes] = useState(project.product_types || DEFAULT_VALUE)

  const { isSearchAILakeAvailable } = useSearchAILakeAvailable(project.product_types)
  const modalStyle = isSearchAILakeAvailable ? { width: '82rem' } : { width: '65rem' }

  return (
    <div data-test-subj='editProjectFeatures'>
      <EuiFlexGroup
        responsive={false}
        gutterSize='m'
        alignItems='center'
        style={{ whiteSpace: 'nowrap' }}
      >
        <EuiFlexItem grow={true} data-test-subj='currentValue'>
          {renderValue()}
        </EuiFlexItem>
        {hasUpdatePermission && <EuiFlexItem grow={false}>{renderEditButton()}</EuiFlexItem>}
      </EuiFlexGroup>
      {renderModal()}
    </div>
  )

  function renderValue() {
    // Currently product_tier is homogenous arcoss product_types, but for
    // defensive purposes, consider any existence of `product_tier: complete` as
    // the project being on "Security complete".
    const isCurrentComplete = (project.product_types || DEFAULT_VALUE).some(
      ({ product_tier }) => product_tier === 'complete',
    )

    const isCurrentEssentials = (project.product_types || DEFAULT_VALUE).some(
      ({ product_tier }) => product_tier === 'essentials',
    )

    if (isCurrentComplete) {
      return <FormattedMessage {...genericMessages.complete} />
    }

    if (isCurrentEssentials) {
      return <FormattedMessage {...genericMessages.essentials} />
    }

    return <FormattedMessage {...genericMessages.aiForSoc} />
  }

  function renderEditButton() {
    return (
      <EuiLink onClick={() => setIsEditing(true)}>
        <FormattedMessage {...genericMessages.edit} />
      </EuiLink>
    )
  }

  function renderModal() {
    if (!isEditing) {
      return null
    }

    const downgradeFromComplete =
      project.product_types &&
      includesProductType(project.product_types, PRODUCT_TYPES.complete.security.obj) &&
      !includesProductType(productTypes, PRODUCT_TYPES.complete.security.obj)
    const downgradeFromEssentials =
      project.product_types &&
      includesProductType(project.product_types, PRODUCT_TYPES.essentials.security.obj) &&
      includesProductType(productTypes, PRODUCT_TYPES.search_ai_lake.ai_soc.obj)
    const shouldRenderDowngradeWarning = downgradeFromComplete || downgradeFromEssentials
    const shouldRenderUpgradeWarning =
      project.product_types &&
      includesProductType(project.product_types, PRODUCT_TYPES.search_ai_lake.ai_soc.obj) &&
      !includesProductType(productTypes, PRODUCT_TYPES.search_ai_lake.ai_soc.obj)

    const onAddOnToggled = (toggledProductType: SecurityProductType) => {
      setProductTypes(toggleSecurityLine(productTypes, toggledProductType))
    }

    const onProductTierChanged = (newProductTier: SecurityProductTier) => {
      setProductTypes(setSecurityTier(newProductTier))
    }

    return (
      <EuiModal maxWidth={modalStyle.width} style={modalStyle} onClose={close}>
        <EuiModalHeader>
          <EuiModalHeaderTitle>
            <FormattedMessage
              id='project.securityProjectFeatures.title'
              defaultMessage='Project features: {name}'
              values={{ name: project.name }}
            />
          </EuiModalHeaderTitle>
        </EuiModalHeader>
        <EuiHorizontalRule
          margin='l'
          style={{ inlineSize: 'auto', marginBlock: 0, marginInline: euiTheme.size.l }}
        />
        <EuiModalBody className='.eui-yScroll'>
          <EuiSpacer size='m' />
          <SecurityProductFeatureOptions
            htmlId={htmlId}
            projectProductTypes={project.product_types}
            productTypes={productTypes}
            onProductTierChanged={onProductTierChanged}
            onAddOnToggled={onAddOnToggled}
          />
        </EuiModalBody>

        <EuiModalFooter>
          <EuiFlexGroup direction='column'>
            {shouldRenderDowngradeWarning && project.product_types?.[0] && productTypes[0] && (
              <EuiFlexItem grow={false}>
                <EuiCallOut
                  title={
                    <FormattedMessage
                      id='project.securityProjectFeatures.callout.downgrade.title'
                      defaultMessage='Changing your Project features selection from {currentFeature} to {newFeature} will cause some features to become unavailable immediately, and data associated with those features may be permanently deleted. {learnMoreLink}'
                      values={{
                        learnMoreLink: (
                          <DocLink link='manageProjectFeatures' showExternalLinkIcon={true}>
                            <FormattedMessage
                              id='project.securityProjectFeatures.callout.learnMore'
                              defaultMessage='Learn more'
                            />
                          </DocLink>
                        ),
                        currentFeature:
                          PRODUCT_TYPES[project.product_types[0].product_tier][
                            project.product_types[0].product_line
                          ].title,
                        newFeature:
                          PRODUCT_TYPES[productTypes[0].product_tier][productTypes[0].product_line]
                            .title,
                      }}
                    />
                  }
                  color='warning'
                  size='s'
                  iconType='alert'
                />
              </EuiFlexItem>
            )}
            {shouldRenderUpgradeWarning && project.product_types?.[0] && productTypes[0] && (
              <EuiFlexItem grow={false}>
                <EuiCallOut
                  title={
                    <FormattedMessage
                      id='project.securityProjectFeatures.callout.upgrade.title'
                      defaultMessage='Changing your Project features selection from {currentFeature} to {newFeature} will not be able to be reverted.'
                      values={{
                        currentFeature:
                          PRODUCT_TYPES[project.product_types[0].product_tier][
                            project.product_types[0].product_line
                          ].title,
                        newFeature:
                          PRODUCT_TYPES[productTypes[0].product_tier][productTypes[0].product_line]
                            .title,
                      }}
                    />
                  }
                  color='warning'
                  size='s'
                  iconType='alert'
                />
              </EuiFlexItem>
            )}
            <EuiFlexItem>
              <EuiFlexGroup gutterSize='m' justifyContent='spaceBetween' alignItems='baseline'>
                <EuiFlexItem grow={false}>
                  <EuiLink
                    href='https://www.elastic.co/pricing/serverless-security'
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    <FormattedMessage
                      id='projects.messages.comparison'
                      defaultMessage='View full plan comparison'
                    />
                  </EuiLink>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiFlexGroup justifyContent='flexEnd'>
                    <EuiFlexItem grow={false}>
                      <EuiButtonEmpty size='s' onClick={close}>
                        <FormattedMessage {...genericMessages.cancel} />
                      </EuiButtonEmpty>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                      <EuiButton
                        size='s'
                        color='primary'
                        fill={true}
                        isLoading={isLoading}
                        onClick={() => {
                          onChange(productTypes, () => setIsEditing(false))
                        }}
                        minWidth='6rem'
                      >
                        <FormattedMessage {...genericMessages.save} />
                      </EuiButton>
                    </EuiFlexItem>
                  </EuiFlexGroup>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiModalFooter>
      </EuiModal>
    )
  }

  function close() {
    setIsEditing(false)
    setProductTypes(project.product_types || DEFAULT_VALUE)
  }
}

export default SecurityProjectFeatures
